import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Modal from 'components/common/modal';
import ScrollEdgeFade from 'components/common/scroll-edge-fade';
import AccessorySelectionModal from 'components/layout/modals/accessory-selection-modal';
import { SelectedSystemType, SystemOptionType } from 'data/types';
import { useComparisonSelection } from 'hooks/use-comparison-selection';
import useCustomSystemRefreshState from 'hooks/use-custom-system-refresh-state';
import useDuctedBundleAdd from 'hooks/use-ducted-bundle-add';
import useDuctedCombinationAdd from 'hooks/use-ducted-combination-add';
import useSystemComparisonSummaries from 'hooks/use-system-comparison-summaries';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { isCustomSystemRefreshPending } from 'utils/custom-system-refresh';
import {
  ductedBundleLeadCombinationId,
  ductedBundleRequestKey,
  findComparedBundleForSlots
} from 'utils/ducted-bundle-compare-identity';
import type { DuctedBundleSlot } from 'utils/ducted-bundle-picks-types';
import { compareStateForCard } from 'utils/ducted-compare-state';
import {
  DuctedSwapLocks,
  DuctedSwapLocksBySystemId
} from 'utils/ducted-component-swap-types';
import {
  ductedSwapLocksFor,
  inheritDuctedSwapLocks,
  withDuctedSwapLocks,
  withoutDuctedSwapLocks
} from 'utils/ducted-swap-locks';
import { notification } from 'utils/ui';
import AddSystemColumnPopover from './add-system-column-popover';
import ColumnLoadingVeil from './column-loading-veil';
import {
  beginColumnAddLoading,
  clearColumnAddLoadingTarget,
  columnAddLoadingForAddSlot,
  columnAddLoadingForSystem,
  ColumnAddLoadingState,
  completeColumnAddLoading,
  IDLE_COLUMN_ADD_LOADING,
  readColumnAddCombinationId,
  reduceColumnAddLoading
} from './comparison-column-loading';
import {
  ColumnTarget,
  PendingColumnSelection,
  placeColumnSystem,
  reconcileComparisonColumns,
  swapColumnSlot,
  SYSTEM_COLUMN_COUNT
} from './comparison-column-slots';
import ComparisonModalTips from './comparison-modal-tips';
import { ComparisonSystemPick } from './comparison-system-pick';
import { DuctedPanelSelection } from './ducted-system-detail-panel';
import { pickKindForSystem, TopPicks } from './equipment-comparison-utils';
import {
  comparisonSectionRowCount,
  showsComparisonZoningLayout
} from './system-column-helpers';
import { SwapListModel } from './swap-list-model';
import SwapSystemPopover from './swap-system-popover';
import SystemColumn from './system-column';
import SystemColumnHeader from './system-column-header';
import useRecommendedComparedIds from './use-recommended-compared-ids';

const EMPTY_COMPARED_ID_BY_CARD_ID: ReadonlyMap<string, string> = new Map();

const ALREADY_COMPARED_MESSAGE = 'That system is already in the comparison.';
const ALREADY_COMPARED_BUNDLE_MESSAGE =
  'That set of systems is already in the comparison.';

type ComparisonModalProps = Readonly<{
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  picks: TopPicks;
  swapListModel: SwapListModel;
  systemNumberById: Map<string, number>;
  comparedIdByCardId?: ReadonlyMap<string, string>;
  onComparedIdMapped?: (
    cardId: string,
    systemId: string,
    selection: DuctedPanelSelection
  ) => void;
}>;

export default function ComparisonModal({
  openState,
  picks,
  swapListModel,
  systemNumberById,
  comparedIdByCardId = EMPTY_COMPARED_ID_BY_CARD_ID,
  onComparedIdMapped
}: ComparisonModalProps) {
  const [open] = openState;
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const configurations = useAppSelector(
    (state) => state.configurations.configurations
  );
  const customSystemRefreshState = useCustomSystemRefreshState();
  const { addSystem, addSystemsToComparison, removeSystem } =
    useComparisonSelection();
  const recommendedIds = useRecommendedComparedIds(comparedIdByCardId);
  const {
    summaries: aiSummaries,
    isLoading: aiSummariesLoading,
    hasError: aiSummariesError
  } = useSystemComparisonSummaries();

  const topFocusRef = useRef<HTMLDivElement | null>(null);
  const columnsScrollRef = useRef<HTMLDivElement | null>(null);

  const [columnIds, setColumnIds] = useState<string[]>([]);
  const [hasSeeded, setHasSeeded] = useState(false);
  const [pendingSelection, setPendingSelection] =
    useState<PendingColumnSelection | null>(null);
  const [columnAddLoading, setColumnAddLoading] =
    useState<ColumnAddLoadingState>(IDLE_COLUMN_ADD_LOADING);
  const [ductedLocksBySystemId, setDuctedLocksBySystemId] =
    useState<DuctedSwapLocksBySystemId>({});
  const compareIdsRef = useRef<string[]>([]);
  compareIdsRef.current = systemsToCompare.map((s) => s.id);
  const columnIdsRef = useRef<string[]>([]);
  columnIdsRef.current = columnIds;

  useEffect(() => {
    setPendingSelection(null);
    setColumnAddLoading(IDLE_COLUMN_ADD_LOADING);
    setDuctedLocksBySystemId({});
    if (!open) {
      setHasSeeded(false);
      return;
    }
    setColumnIds(compareIdsRef.current);
    setHasSeeded(true);
  }, [open]);

  const systemById = useMemo(() => {
    const map = new Map<string, SystemOptionType>();
    systemsToCompare.forEach((system) => map.set(system.id, system));
    [
      ...swapListModel.picks.list,
      ...swapListModel.customSystems,
      ...swapListModel.moreSystems
    ].forEach((system) => map.set(system.id, system));
    return map;
  }, [systemsToCompare, swapListModel]);

  const availableIds = useMemo(() => {
    const ids = new Set<string>();
    swapListModel.picks.list.forEach((system) => ids.add(system.id));
    swapListModel.customSystems.forEach((system) => ids.add(system.id));
    swapListModel.moreSystems.forEach((system) => ids.add(system.id));
    return ids;
  }, [swapListModel]);

  useEffect(() => {
    if (!open || !hasSeeded || pendingSelection === null) return;
    const { slotIndex, uniqueId } = pendingSelection;
    if (!availableIds.has(uniqueId)) return;
    setColumnIds((current) => swapColumnSlot(current, slotIndex, uniqueId));
    setPendingSelection(null);
  }, [open, hasSeeded, pendingSelection, availableIds]);

  const renderedColumns = useMemo(
    () =>
      columnIds
        .map((id, slotIndex) => ({
          system: systemById.get(id) ?? null,
          slotIndex
        }))
        .filter(
          (entry): entry is { system: SystemOptionType; slotIndex: number } =>
            entry.system !== null
        ),
    [columnIds, systemById]
  );

  const pendingPlacementIdsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!open || !hasSeeded) return;
    columnIds.forEach((id) => pendingPlacementIdsRef.current.delete(id));
    const { idsToRemove, systemsToAdd } = reconcileComparisonColumns(
      columnIds,
      renderedColumns.map(({ system }) => system),
      systemsToCompare,
      pendingPlacementIdsRef.current
    );
    idsToRemove.forEach((id) => removeSystem(id));
    systemsToAdd.forEach((system) => {
      addSystem(system, pickKindForSystem(picks, system), {
        skipConfirmations: true
      });
    });
  }, [
    open,
    hasSeeded,
    columnIds,
    renderedColumns,
    systemsToCompare,
    picks,
    addSystem,
    removeSystem
  ]);

  const disabledIdsBySlot = useMemo(
    () =>
      columnIds.map(
        (_, slotIndex) =>
          new Set(columnIds.filter((id, index) => index !== slotIndex))
      ),
    [columnIds]
  );

  const ductedTargetsByCardIdRef = useRef(new Map<string, ColumnTarget>());

  const pendingCompatibilityAddIds = useAppSelector(
    (state) => state.systems.pendingCompatibilityAddIds
  );

  const placeAddedSystems = useCallback(
    (
      target: ColumnTarget,
      added: SelectedSystemType[]
    ): SelectedSystemType | null => {
      const [system] = added;
      if (!system) return null;
      const placement = placeColumnSystem(
        columnIdsRef.current,
        target,
        system.id
      );
      if (!placement.placed) {
        added.forEach((entry) => removeSystem(entry.id));
        return null;
      }
      columnIdsRef.current = placement.columns;
      pendingPlacementIdsRef.current.add(system.id);
      setColumnIds(
        (current) => placeColumnSystem(current, target, system.id).columns
      );
      setDuctedLocksBySystemId((current) =>
        inheritDuctedSwapLocks(current, target, system.id)
      );
      addSystemsToComparison(added);
      return system;
    },
    [removeSystem, addSystemsToComparison]
  );

  const { requestAdd: requestDuctedAdd, accessorySelection } =
    useDuctedCombinationAdd({
      onAdded: (added, selection) => {
        setColumnAddLoading((current) =>
          completeColumnAddLoading(current, selection.cardId)
        );
        const target: ColumnTarget = ductedTargetsByCardIdRef.current.get(
          selection.cardId
        ) ?? { kind: 'add' };
        ductedTargetsByCardIdRef.current.delete(selection.cardId);
        const system = placeAddedSystems(target, added);
        if (!system) return;
        onComparedIdMapped?.(selection.cardId, system.id, selection);
      },
      onSettled: (selection) => {
        setColumnAddLoading((current) =>
          completeColumnAddLoading(current, selection.cardId)
        );
      }
    });

  const bundleTargetRef = useRef<ColumnTarget>({ kind: 'add' });
  const bundleRequestKeyRef = useRef<string | null>(null);

  const completeBundleLoading = () => {
    const requestKey = bundleRequestKeyRef.current;
    if (requestKey === null) return;
    setColumnAddLoading((current) =>
      completeColumnAddLoading(current, requestKey)
    );
  };

  const {
    beginAdd: beginBundleAdd,
    accessorySelection: bundleAccessorySelection
  } = useDuctedBundleAdd({
    onAdded: (added) => {
      completeBundleLoading();
      placeAddedSystems(bundleTargetRef.current, added);
    },
    onSettled: () => {
      completeBundleLoading();
      bundleRequestKeyRef.current = null;
    }
  });

  const accessoryPickerOpen =
    accessorySelection.openState[0] || bundleAccessorySelection.openState[0];

  useEffect(() => {
    setColumnAddLoading((current) =>
      reduceColumnAddLoading(current, {
        pipelineActive:
          current.request !== null &&
          pendingCompatibilityAddIds.some(
            (id) => Number(id) === current.request?.combinationId
          ),
        accessoryPickerOpen
      })
    );
  }, [pendingCompatibilityAddIds, accessoryPickerOpen]);

  const placeDuctedSelection = useCallback(
    (target: ColumnTarget, selection: DuctedPanelSelection) => {
      const { comparedId } = compareStateForCard(
        comparedIdByCardId,
        systemsToCompare,
        selection
      );
      if (comparedId !== undefined) {
        setColumnAddLoading((current) =>
          clearColumnAddLoadingTarget(current, target)
        );
        if (!columnIds.includes(comparedId)) {
          setColumnIds(
            (current) => placeColumnSystem(current, target, comparedId).columns
          );
          setDuctedLocksBySystemId((current) =>
            inheritDuctedSwapLocks(current, target, comparedId)
          );
        }
        return;
      }
      const combinationId = readColumnAddCombinationId(selection.combination);
      if (combinationId !== null) {
        setColumnAddLoading(
          beginColumnAddLoading(target, combinationId, selection.cardId)
        );
      }
      ductedTargetsByCardIdRef.current.set(selection.cardId, target);
      requestDuctedAdd(selection);
    },
    [comparedIdByCardId, systemsToCompare, columnIds, requestDuctedAdd]
  );

  const handleDuctedComponentSwap = useCallback(
    (replaceSystemId: string, selection: DuctedPanelSelection) => {
      const { comparedId } = compareStateForCard(
        comparedIdByCardId,
        systemsToCompare,
        selection
      );
      if (comparedId !== undefined && columnIds.includes(comparedId)) {
        notification(ALREADY_COMPARED_MESSAGE, 'warning');
        return;
      }
      placeDuctedSelection({ kind: 'swap', replaceSystemId }, selection);
    },
    [comparedIdByCardId, systemsToCompare, columnIds, placeDuctedSelection]
  );

  const placeDuctedBundle = useCallback(
    (target: ColumnTarget, slots: DuctedBundleSlot[]) => {
      const compared = findComparedBundleForSlots(systemsToCompare, slots);
      if (compared !== undefined) {
        setColumnAddLoading((current) =>
          clearColumnAddLoadingTarget(current, target)
        );
        if (columnIds.includes(compared.id)) {
          notification(ALREADY_COMPARED_BUNDLE_MESSAGE, 'warning');
          return;
        }
        setColumnIds(
          (current) => placeColumnSystem(current, target, compared.id).columns
        );
        setDuctedLocksBySystemId((current) =>
          inheritDuctedSwapLocks(current, target, compared.id)
        );
        return;
      }
      const requestKey = ductedBundleRequestKey(slots);
      bundleTargetRef.current = target;
      bundleRequestKeyRef.current = requestKey;
      if (!beginBundleAdd()) {
        bundleRequestKeyRef.current = null;
        return;
      }
      const combinationId = ductedBundleLeadCombinationId(slots);
      if (combinationId !== null) {
        setColumnAddLoading(
          beginColumnAddLoading(target, combinationId, requestKey)
        );
      }
    },
    [systemsToCompare, columnIds, beginBundleAdd]
  );

  const handlePick = useCallback(
    (target: ColumnTarget, pick: ComparisonSystemPick) => {
      if (pick.kind === 'ducted') {
        placeDuctedSelection(target, pick.selection);
        return;
      }
      if (pick.kind === 'bundle') {
        placeDuctedBundle(target, pick.slots);
        return;
      }
      if (!columnIds.includes(pick.system.id)) {
        setColumnIds(
          (current) =>
            placeColumnSystem(current, target, pick.system.id).columns
        );
      }
    },
    [columnIds, placeDuctedSelection, placeDuctedBundle]
  );

  const handleRemoveColumn = useCallback(
    (slotIndex: number, systemId: string) => {
      setColumnAddLoading((current) =>
        clearColumnAddLoadingTarget(current, {
          kind: 'swap',
          replaceSystemId: systemId
        })
      );
      setColumnIds((current) =>
        current.filter((_, index) => index !== slotIndex)
      );
      setDuctedLocksBySystemId((current) =>
        withoutDuctedSwapLocks(current, systemId)
      );
      removeSystem(systemId);
    },
    [removeSystem]
  );

  const handleDuctedLocksChange = useCallback(
    (systemId: string, locks: DuctedSwapLocks) => {
      setDuctedLocksBySystemId((current) =>
        withDuctedSwapLocks(current, systemId, locks)
      );
    },
    []
  );

  const columnIdSet = useMemo(() => new Set(columnIds), [columnIds]);

  const placeholderSlots = useMemo(
    () =>
      Array.from(
        { length: Math.max(0, SYSTEM_COLUMN_COUNT - renderedColumns.length) },
        (_, index) => renderedColumns.length + index
      ),
    [renderedColumns.length]
  );

  const totalColumns = renderedColumns.length + placeholderSlots.length;
  const gridTemplateColumns = `repeat(${totalColumns}, minmax(0, 1fr))`;
  const sectionRowCount = comparisonSectionRowCount(
    renderedColumns.map(({ system }) => system),
    configurations
  );

  return (
    <Modal
      openState={openState}
      title='Compare Selected Systems'
      size='fullscreen'
      infoMode
      initialFocus={topFocusRef}
    >
      <div
        id='comparison-modal-content'
        ref={topFocusRef}
        tabIndex={-1}
        className='outline-none'
      >
        <ComparisonModalTips />
        <div className='relative'>
          <div
            ref={columnsScrollRef}
            className='max-h-[calc(90vh-7rem)] overflow-y-auto'
          >
            <div
              className='sticky top-0 z-30 grid gap-4 bg-white'
              style={{ gridTemplateColumns }}
            >
              {renderedColumns.map(({ system, slotIndex }) => {
                const optionLoading = columnAddLoadingForSystem(
                  columnAddLoading,
                  system.id
                );
                return (
                  <div key={system.id} className='relative min-w-0'>
                    <div
                      className={clsx(
                        'h-full',
                        optionLoading && 'pointer-events-none'
                      )}
                    >
                      <SystemColumnHeader
                        system={system}
                        pickKind={pickKindForSystem(picks, system)}
                        systemNumber={
                          systemNumberById.get(system.id) ?? slotIndex + 1
                        }
                        recommended={recommendedIds.has(system.id)}
                        swapPopover={
                          <SwapSystemPopover
                            swapListModel={swapListModel}
                            comparedIdByCardId={comparedIdByCardId}
                            selectedId={system.id}
                            disabledIds={disabledIdsBySlot[slotIndex]}
                            onPick={(pick) =>
                              handlePick(
                                { kind: 'swap', replaceSystemId: system.id },
                                pick
                              )
                            }
                          />
                        }
                        onRemove={() =>
                          handleRemoveColumn(slotIndex, system.id)
                        }
                      />
                    </div>
                    {optionLoading && (
                      <ColumnLoadingVeil showSpinner className='rounded-t-lg' />
                    )}
                  </div>
                );
              })}
              {placeholderSlots.map((slot) => {
                const optionLoading = columnAddLoadingForAddSlot(
                  columnAddLoading,
                  slot,
                  renderedColumns.length
                );
                return (
                  <div
                    key={`add-header-${slot}`}
                    className='relative h-full min-w-0'
                  >
                    <div
                      className={clsx(
                        'h-full',
                        optionLoading && 'pointer-events-none'
                      )}
                    >
                      <AddSystemColumnPopover
                        swapListModel={swapListModel}
                        comparedIdByCardId={comparedIdByCardId}
                        selectedId={null}
                        disabledIds={columnIdSet}
                        onPick={(pick) => handlePick({ kind: 'add' }, pick)}
                      />
                    </div>
                    {optionLoading && (
                      <ColumnLoadingVeil showSpinner className='rounded-lg' />
                    )}
                  </div>
                );
              })}
              <ScrollEdgeFade
                scrollRef={columnsScrollRef}
                edge='top'
                className='inset-x-0 top-full h-8'
              />
            </div>
            <div
              className={clsx(
                'grid gap-4',
                sectionRowCount === 4
                  ? '[grid-template-rows:repeat(4,auto)]'
                  : '[grid-template-rows:repeat(3,auto)]'
              )}
              style={{ gridTemplateColumns }}
            >
              {renderedColumns.map(({ system, slotIndex }) => (
                <SystemColumn
                  key={system.id}
                  system={system}
                  sectionRowCount={sectionRowCount}
                  showZoningLayout={showsComparisonZoningLayout(
                    system,
                    configurations
                  )}
                  isRefreshPending={isCustomSystemRefreshPending(
                    customSystemRefreshState,
                    system.configuration.uniqueId
                  )}
                  optionLoading={columnAddLoadingForSystem(
                    columnAddLoading,
                    system.id
                  )}
                  onCustomSystemSaved={(uniqueId) =>
                    setPendingSelection({ slotIndex, uniqueId })
                  }
                  onDuctedComponentSwap={(selection) =>
                    handleDuctedComponentSwap(system.id, selection)
                  }
                  ductedLockState={{
                    locks: ductedSwapLocksFor(ductedLocksBySystemId, system.id),
                    onChange: (locks) =>
                      handleDuctedLocksChange(system.id, locks)
                  }}
                  aiSummary={aiSummaries[system.id]}
                  aiSummaryLoading={aiSummariesLoading}
                  aiSummaryError={aiSummariesError}
                />
              ))}
              {columnAddLoading.request?.target.kind === 'add' &&
                renderedColumns.length < SYSTEM_COLUMN_COUNT && (
                  <div
                    aria-hidden
                    className={clsx(
                      'relative min-h-64 rounded-b-lg border-x border-b border-gray-200 bg-white',
                      sectionRowCount === 4
                        ? '[grid-row:span_4]'
                        : '[grid-row:span_3]'
                    )}
                  >
                    <ColumnLoadingVeil className='rounded-b-lg' />
                  </div>
                )}
            </div>
          </div>
          <ScrollEdgeFade
            scrollRef={columnsScrollRef}
            edge='bottom'
            className='inset-x-0 bottom-0 z-30 h-8'
          />
        </div>
      </div>
      <AccessorySelectionModal
        openState={accessorySelection.openState}
        combinationsWithChoices={accessorySelection.combinationsWithChoices}
        systemCount={accessorySelection.systemCount}
        onConfirm={accessorySelection.onConfirm}
      />
      <AccessorySelectionModal
        openState={bundleAccessorySelection.openState}
        combinationsWithChoices={
          bundleAccessorySelection.combinationsWithChoices
        }
        systemCount={bundleAccessorySelection.systemCount}
        onConfirm={bundleAccessorySelection.onConfirm}
      />
    </Modal>
  );
}
